import { createFileRoute } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { useCreateInventoryTransfer } from '@/api/api-mutations'
import { useListInventoryLocations } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { Text } from '@/components/base/text'
import { useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

import { useVariantLineDraft, VariantSearchInput, type VariantDraftLine } from '../-components'

export const Route = createFileRoute('/_authenticated/_general/inventory/transfers/create')({
	component: CreateTransferRoute,
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.CreateTransfer()
	}
})

function CreateTransferRoute() {
	return (
		<RouteModal asDrawer prev='..'>
			<CreateTransferForm />
		</RouteModal>
	)
}

function CreateTransferForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()
	const createTransfer = useCreateInventoryTransfer(true)
	const inventoryLocations = useListInventoryLocations({ limit: 100, offset: 0 })
	const linesRef = useRef<VariantDraftLine[]>([])

	const form = useAppForm({
		defaultValues: {
			fromUuid: '',
			toUuid: '',
			notes: ''
		},
		onSubmit: async ({ value }) => {
			const lines = linesRef.current
			if (lines.length === 0 || value.fromUuid === value.toUuid) return
			const result = await createTransfer.mutateAsync({
				body: {
					fromInventoryLocationUuid: value.fromUuid,
					toInventoryLocationUuid: value.toUuid,
					notes: value.notes || undefined,
					lines: lines.map(l => ({ variantUuid: l.variantUuid, quantity: Number(l.quantity) }))
				}
			})
			createdToast(L.Inventory.CreateTransfer(), {
				to: '/inventory/transfers/$transferUuid',
				params: { transferUuid: result.response.uuid }
			})
			handleSuccess()
		}
	})

	const locationOptions = (inventoryLocations.data?.inventoryLocations ?? []).map(location => ({
		value: location.uuid,
		label: location.name
	}))

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Inventory.CreateTransfer()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
							<form.AppField
								name='fromUuid'
								validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
								children={field => (
									<field.SelectField label={L.Inventory.Column.From()} options={locationOptions} />
								)}
							/>
							<form.Subscribe selector={state => state.values.fromUuid}>
								{fromUuid => (
									<form.AppField
										name='toUuid'
										validators={{
											onChange: ({ value }) => {
												if (!value) return L.General.Required()
												if (value === fromUuid) return L.General.Required()
												return undefined
											}
										}}
										children={field => (
											<field.SelectField
												label={L.Inventory.Column.To()}
												options={locationOptions.filter(
													location => location.value !== fromUuid
												)}
											/>
										)}
									/>
								)}
							</form.Subscribe>
							<form.AppField
								name='notes'
								children={field => <field.TextField label={L.Inventory.Column.Reason()} optional />}
							/>
						</div>

						<form.Subscribe selector={state => state.values.fromUuid}>
							{fromUuid => (
								<TransferLinesEditor
									fromUuid={fromUuid}
									onLinesChange={lines => {
										linesRef.current = lines
									}}
								/>
							)}
						</form.Subscribe>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small' type='button'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}

function TransferLinesEditor({
	fromUuid,
	onLinesChange
}: {
	fromUuid: string
	onLinesChange: (lines: VariantDraftLine[]) => void
}) {
	const { L } = useI18n()
	const { search, setSearch, lines, addLine, updateLine, removeLine, variantResults } =
		useVariantLineDraft<VariantDraftLine>({
			active: true,
			inventoryLocationUuid: fromUuid,
			createLine: base => base
		})

	useEffect(() => {
		onLinesChange(lines)
	}, [lines, onLinesChange])

	return (
		<>
			<VariantSearchInput
				search={search}
				setSearch={setSearch}
				disabled={fromUuid === ''}
				results={variantResults.data?.variants ?? []}
				onSelect={variant => addLine(variant.uuid, variant.productTitle, variant.sku)}
				renderResultMeta={variant => (
					<span className='text-xs text-ui-fg-muted'>
						{variant.sku ? `${variant.sku} · ` : ''}
						{variant.quantityOnHand != null
							? `${L.Inventory.Column.QuantityOnHand()}: ${variant.quantityOnHand}`
							: ''}
					</span>
				)}
			/>
			<div className='flex flex-col gap-2'>
				{lines.length === 0 && <Text className='text-sm text-ui-fg-muted'>{L.Inventory.NoLines()}</Text>}
				{lines.map(line => (
					<div key={line.variantUuid} className='grid grid-cols-12 items-center gap-2 text-sm'>
						<div className='col-span-8 min-w-0'>
							<div className='truncate'>{line.productTitle}</div>
							{line.sku && <div className='truncate text-xs text-ui-fg-muted'>{line.sku}</div>}
						</div>
						<div className='col-span-3'>
							<Input
								type='number'
								step='any'
								placeholder={L.Inventory.Column.Quantity()}
								value={line.quantity}
								onChange={e => updateLine(line.variantUuid, { quantity: e.target.value })}
							/>
						</div>
						<div className='col-span-1 flex justify-end'>
							<IconButton
								size='small'
								variant='transparent'
								tooltipText={L.General.Delete()}
								onClick={() => removeLine(line.variantUuid)}
							>
								<Trash2 className='size-4' />
							</IconButton>
						</div>
					</div>
				))}
			</div>
		</>
	)
}
